
import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { GalaxyConfirmationModalModule } from '@vendasta/galaxy/confirmation-modal';
import { ObjectType, TranslationModule } from '@galaxy/crm/static';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';

import { MatInput } from '@angular/material/input';
import { GalaxyTagsModule } from '@vendasta/galaxy/tags';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { catchError, expand, finalize, Observable, of, reduce, take, takeWhile } from 'rxjs';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { CampaignInterface } from '@vendasta/vendasta-yesware-bridge/lib/_internal/interfaces/campaigns.interface';
import { CampaignActionsService } from '../campaign-actions.service';
import { ListUsersCampaignsResponse } from '@vendasta/vendasta-yesware-bridge/lib/_internal/objects';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { NamespaceInterface } from '../campaign-actions.interfaces';

export interface DialogData {
  namespace: NamespaceInterface;
  objectType: ObjectType;
  numberOfRows: number;
}

@Component({
  imports: [
    GalaxyConfirmationModalModule,
    MatButtonModule,
    MatIconModule,
    MatDialogClose,
    TranslationModule,
    GalaxyFormFieldModule,
    MatInput,
    ReactiveFormsModule,
    GalaxyTagsModule,
    GalaxyTooltipModule,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    GalaxyPipesModule,
    GalaxyLoadingSpinnerModule
],
  selector: 'crm-select-campaign-modal',
  templateUrl: `./select-campaign-modal.component.html`,
  styleUrls: [`./select-campaign-modal.component.scss`],
  providers: [],
})
export class SelectCampaignModalComponent implements OnInit {
  public dialogRef = inject(MatDialogRef<SelectCampaignModalComponent>);
  private readonly campaignActionsService = inject(CampaignActionsService);
  private readonly dialogData: DialogData = inject(MAT_DIALOG_DATA);
  numberOfRows = this.dialogData?.numberOfRows ?? 0;
  isLoading = false;

  options: CampaignInterface[] = [];
  selectedListControl = new FormControl<number>(0, { nonNullable: true, validators: [Validators.required] });

  @ViewChild('input') input: ElementRef<HTMLInputElement> | undefined;

  ngOnInit(): void {
    this.isLoading = true;
    this.fetchAllCampaigns(this.dialogData.namespace)
      .pipe(
        take(1),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe((campaigns) => {
        this.options = campaigns ?? [];
      });
  }

  private fetchCampaigns(
    namespace: NamespaceInterface,
    cursor: string,
    pageSize: number,
  ): Observable<ListUsersCampaignsResponse> {
    return this.campaignActionsService.listCampaigns(namespace, {
      cursor: cursor,
      pageSize: pageSize,
    });
  }

  private fetchAllCampaigns(namespace: NamespaceInterface): Observable<CampaignInterface[]> {
    return this.fetchCampaigns(namespace, '', 100).pipe(
      expand((response) => this.fetchCampaigns(namespace, response.pagingMetadata?.nextCursor, 100)),
      takeWhile((response) => !!response.pagingMetadata?.hasMore, true),
      reduce((all, { campaigns = [] }) => all.concat(campaigns), [] as CampaignInterface[]),
      catchError(() => {
        return of([]);
      }),
    );
  }

  optionValues(): void {
    const optionValue = this.input?.nativeElement.value.toLowerCase() || '';
    this.options = this.options.filter((o) => o?.name?.toLowerCase().includes(optionValue));
  }

  addToCampaign() {
    if (this.selectedListControl.invalid) {
      return;
    }
    const selectedCampaign = this.options.find((campaign) => campaign.campaignId === this.selectedListControl.value);
    this.dialogRef.close(selectedCampaign);
  }

  displayFn(options: CampaignInterface[]): (campaignID: number) => string {
    return (campaignID: number): string => {
      return options?.find((option) => option.campaignId === campaignID)?.name || '';
    };
  }
}
